<template>
  <CardTable
    :title="title"
    :subTitle="subTitle"
    :searchText="searchText"
    :columns="tableColumns"
    :rows="table.rows"
    :isLoading="table.isLoading"
    :pageSize="serverParams.pageSize"
    :pageNumber="serverParams.pageNumber"
    :totalPage="table.totalPage"
    :totalRecords="table.totalRecords"
    @onRowSelected="onRowSelected"
    @onPerPageChange="onPerPageChange"
    @onPageChange="onPageChange"
    @onSearch="onSearch"
  >
    <template #toolbar>
      <b-button variant="primary" :to="{ name: formName }">
        Registrasi Member Baru
      </b-button>
    </template>
    <!-- Store anak "mewarisi" member store induknya (dikonfigurasi lewat Store Anak di
         Backoffice), jadi daftar ini bisa memuat member yang bukan milik store aktif.
         Pilihannya hanya ditampilkan kalau store ini memang punya induk — kalau tidak,
         kedua mode mengembalikan data yang sama persis. -->
    <template #info>
      <treeselect
        v-if="hasParentStore"
        class="d-inline-block mr-3 mb-2 text-left"
        style="width: 220px"
        placeholder="Cakupan Store"
        v-model="storeScope"
        :clearable="false"
        :options="storeScopeOptions"
        @input="onStoreScopeChange"
      />
    </template>
    <template #empty>
      <EmptyTable
        title="Belum ada data member"
        :description="subTitle"
        button-label="Registrasi Member Baru"
        :href="{ name: formName }"
      />
    </template>
    <template #cell(image)="data">
      <div
        v-if="data.item.image != null"
        @click="show(data.item.image?.label)"
        class="symbol symbol-45 symbol-light"
      >
        <span class="symbol-label">
          <img :src="mediaUrl(data.item.image.label)" height="32" />
        </span>
      </div>
    </template>
    <template #cell(store)="data">
      {{ data.item.store?.label ?? "-" }}
      <br /><small v-if="data.item.store?.id && data.item.store.id != store.id">
        Store induk
      </small>
    </template>
    <template #cell(identity)="data">
      <span>
        {{ data.item?.nomorIdentitas ?? "-" }}
      </span>
      <br /><small>
        {{ data.item?.identityType?.label ?? "-" }}
      </small>
    </template>
    <template #cell(gender)="data">
      {{ data.value.label }}
    </template>
    <template #cell(birth)="data">
      <span v-if="!data.item.placeOfBirth && !data.item.dateOfBirth">-</span>
      <span v-else>
        {{
          `${data.item.placeOfBirth ?? "-"}, ${dateFormat(
            data.item.dateOfBirth,
            "DD MMMM YYYY"
          )}`
        }}
      </span>
    </template>
    <template #cell(createdAt)="data">
      {{ dateFormat(data.item.created.createdAt, "DD MMMM YYYY") }}
    </template>
    <template #cell(memberCard)="data">
      {{ data.value.cardNumber }}
      <br /><small>{{
        dateRange(data.value.validFrom, data.value.validUntil)
      }}</small>
    </template>
  </CardTable>

  <!-- <viewer :images="images">
    <img v-for="src in images" :key="src" :src="src" />
  </viewer> -->
</template>

<script>
import { mapGetters } from "vuex";
import { member as columns } from "@/core/datasource/column";
import { pageSize, mediaUrl, dateFormat, dateRange } from "@/core/utils";

export default {
  data: () => ({
    formName: "memberCreate",
    title: "Member",
    subTitle: "Semua member akan ditampilkan disini ya",
    searchText: "Cari member",
    serverParams: {
      pageNumber: 1,
      pageSize,
      fullName: null,
      actived: null,
      orderBy: "createdAt desc",
      // Dikirim eksplisit supaya cakupannya tidak bergantung pada perilaku default
      // backend. true = sama dengan perilaku POS sebelumnya (member store induk ikut
      // tampil, dibutuhkan resepsionis store anak untuk melayani member induk).
      includeParentStore: true,
    },
    storeScope: "withParent",
    storeScopeOptions: [
      { id: "own", label: "Hanya Store Ini" },
      { id: "withParent", label: "Termasuk Store Induk" },
    ],
    hasParentStore: false,
    table: {
      isLoading: false,
      columns,
      rows: [],
      totalPage: 0,
      totalRecords: 0,
    },
    images: [],
  }),
  computed: {
    ...mapGetters(["store"]),
    // Kolom Store hanya berguna saat daftar memuat member dari lebih dari satu store.
    tableColumns() {
      const self = this;
      if (!self.serverParams.includeParentStore) return self.table.columns;

      const index = self.table.columns.findIndex((x) => x.key == "fullName");
      const storeColumn = {
        label: "Store",
        key: "store",
        thClass: "align-middle",
        tdClass: "align-middle",
      };
      return [
        ...self.table.columns.slice(0, index + 1),
        storeColumn,
        ...self.table.columns.slice(index + 1),
      ];
    },
  },
  watch: {
    store() {
      this.getStoreRelation();
      this.getAll();
    },
  },
  created() {
    this.getStoreRelation();
    this.getAll();
  },
  methods: {
    mediaUrl,
    dateFormat,
    dateRange,
    show(image) {
      this.$viewerApi({
        images: image,
      });
    },
    getAll() {
      const self = this;

      self.table.isLoading = true;
      self.$store
        .dispatch("apis/get", {
          url: `store/${self.store.id}/member`,
          params: self.serverParams,
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({
              zIndex: 10000,
              message: response.message,
            });
          } else {
            self.table.rows = response.data.data;
            self.table.totalPage = response.data.totalPage;
            self.table.totalRecords = response.data.totalData;
          }
          self.table.isLoading = false;
        });
    },
    // Store aktif tidak selalu punya induk; kalau tidak ada, pilihan cakupan
    // disembunyikan karena kedua modenya mengembalikan data yang sama.
    // Kalau detail store-nya sendiri tidak bisa diambil (endpoint ini membatasi user
    // non-owner ke store yang terdaftar di UserStores, jadi bisa 404), pilihannya tetap
    // ditampilkan — lebih baik ada tombol yang mungkin tidak berguna daripada fiturnya
    // hilang diam-diam.
    getStoreRelation() {
      const self = this;

      self.$store
        .dispatch("apis/get", { url: `store/${self.store.id}` })
        .then((response) => {
          self.hasParentStore = response.error
            ? true
            : (response.data?.parentStoreIds ?? []).length > 0;
        });
    },
    onStoreScopeChange() {
      const self = this;
      self.updateParams({
        includeParentStore: self.storeScope == "withParent",
        pageNumber: 1,
      });
      self.getAll();
    },
    updateParams(newProps) {
      const self = this;
      self.serverParams = Object.assign({}, self.serverParams, newProps);
    },
    onRowSelected(items) {
      const self = this;
      self.$router.push({
        name: "memberUpdate",
        params: {
          id: items[0].id,
        },
      });
    },
    onPerPageChange(params) {
      const self = this;
      self.updateParams({ pageSize: params, pageNumber: 1 });
      self.getAll();
    },
    onPageChange(params) {
      const self = this;
      self.updateParams({ pageNumber: params });
      self.getAll();
    },
    onSearch(params) {
      const self = this;
      self.updateParams({
        fullName: params,
      });
      self.getAll();
    },
    onFilter() {
      const self = this;
      self.getAll();
    },
  },
};
</script>
